import React, { useRef } from 'react';
import { Box, Divider, TextField, Typography } from '@mui/material';
import {
  FormProps,
  FormHeader,
  ClientNameField,
  DocumentUploadArea,
  AudioRecordingSection,
  WritingStyleSelector,
  InfoTip,
} from './shared';

const ReclamacaoTrabalhistaForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedStyle, setSelectedStyle] = React.useState(values.estilo_escrita || 'padrao');

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  const handleRecordingComplete = (audioBlob: Blob) => {
    const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, {
      type: 'audio/webm',
    });
    onFileUpload([audioFile]);
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Reclamação Trabalhista"
        customDescription="Petição inicial para ações trabalhistas contra empregadores"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* 1. Nome do Cliente */}
        <ClientNameField
          value={values.nome_cliente || ''}
          onChange={(value) => handleFieldChange('nome_cliente', value)}
          label="Escreva o nome do seu cliente:"
        />

        {/* 2. Fatos do Caso (Obrigatório) */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Quais são os fatos do caso? <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={8}
            placeholder="Ex: O autor trabalhou para a empresa reclamada de 01/02/2020 a 15/08/2024, exercendo a função de operador de produção, com jornada das 08h às 18h, de segunda a sábado. Durante o contrato, não foram pagas as horas extras realizadas, nem concedidos os intervalos intrajornada. O autor foi dispensado sem justa causa e não recebeu as verbas rescisórias devidas..."
            value={values.fatos_caso || ''}
            onChange={(e) => handleFieldChange('fatos_caso', e.target.value)}
            inputProps={{ maxLength: 10000 }}
          />
          <Typography 
            variant="caption" 
            color={(values.fatos_caso || '').length >= 200 ? 'success.main' : 'text.secondary'} 
            sx={{ mt: 0.5, display: 'block' }}
          >
            {(values.fatos_caso || '').length} de 10000 caracteres permitidos
            {(values.fatos_caso || '').length < 200 && ` (mínimo: 200)`}
            {(values.fatos_caso || '').length >= 200 && ' ✓'}
          </Typography>
        </Box>

        {/* 3. Parte Contrária (Empregador) */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Escreva o nome da parte contrária: <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            placeholder="Ex: A reclamada é a empresa XYZ Indústria e Comércio Ltda."
            value={values.parte_contraria || ''}
            onChange={(e) => handleFieldChange('parte_contraria', e.target.value)}
            helperText="Nome da empresa ou empregador reclamado"
          />
        </Box>

        {/* 4. Upload de Documentos */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="CTPS, contracheques, contrato de trabalho, TRCT, comprovantes de jornada, advertências, e-mails e mensagens relacionadas."
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />

        {/* Gravação de Áudio */}
        <AudioRecordingSection
          onRecordingComplete={handleRecordingComplete}
          description="Grave um áudio descrevendo os fatos trabalhistas que a IA irá transcrever automaticamente."
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Estilo de Escrita */}
      <WritingStyleSelector
        selectedStyle={selectedStyle}
        onStyleChange={(styleId) => {
          setSelectedStyle(styleId);
          handleFieldChange('estilo_escrita', styleId);
        }}
      />

      <InfoTip message="Na reclamação trabalhista, detalhe bem o período de trabalho, função exercida, jornada, salário e todas as verbas que estão sendo pleiteadas. Anexe documentos que comprovem o vínculo empregatício e as irregularidades." />
    </Box>
  );
};

export default ReclamacaoTrabalhistaForm;

